import type { Printing } from "@webscan/catalogue.js";

/**
 * What a printing is worth to this app: the better of its two finishes.
 *
 * The scanner cannot see foil, so a printing is valued at the higher of its plain and
 * foil prices — better a second look at a cheap non-foil than a missed foil — in USD
 * when it has any USD price, EUR otherwise. Shared beneath both sides because the same
 * number has to drive the row's value, the chime and the price-tie policy: a policy
 * that compared a different price from the one the row shows would be lying to one of
 * them.
 */
export type PotentialPrice = {
  /** Integer cents; null when the printing carries no price in either finish. */
  cents: number | null;
  currency: "USD" | "EUR";
  /** Whether the foil finish is the one being quoted. */
  foil: boolean;
  /** The plain finish in the same currency, for the caption under a foil quote. */
  plain: number | null;
};

type Finishes = Pick<Printing, "usd" | "usdFoil" | "eur" | "eurFoil">;

/** Foil is a reason to take a second look; the scanner cannot observe the finish. */
export function potentialPrice(printing: Finishes): PotentialPrice {
  const currency = printing.usd !== null || printing.usdFoil !== null ? "USD" : "EUR";
  const plain = currency === "USD" ? printing.usd : printing.eur;
  const foil = currency === "USD" ? printing.usdFoil : printing.eurFoil;
  const betterFoil = foil !== null && (plain === null || foil > plain);
  return { cents: betterFoil ? foil : plain, currency, foil: betterFoil, plain };
}
